import { useCallback, useEffect, useMemo, useState } from 'react';

import { isEqual } from 'lodash-es';

import { useServiceModeContext } from 'state/service-mode';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { initStore2_0 } from 'utils/preload-store';
import { remapStore } from 'utils/restaurant';

import { GetStoreInformation, IPreloadedStoreState2_0, IStoreContext, IUseAnyStore } from './types';
import { resetLastTimeStoreUpdated } from './use-store-utils';

const preloadedStoreInfo = (): IPreloadedStoreState2_0 | null =>
  LocalStorage.getItem(StorageKeys.STORE_VERSION_2);

/**
 * Store Selection 2.0
 *
 * A user will be able to select any store, regardless
 * of availability.
 *
 * This hook will expose the availability of the store so that
 * we may hide/show content based on the store's state.
 */
export const useAnyStore = (): IUseAnyStore => {
  const { serviceMode } = useServiceModeContext();
  const preloaded = useMemo(() => preloadedStoreInfo(), []);
  const [store, _setStore] = useState(initStore2_0(preloaded, serviceMode));
  const remappedStore = useMemo(() => remapStore(store), [store]);

  const setStore = useCallback(
    newStore =>
      _setStore(s => {
        if (s) {
          // @ts-ignore need this because hasSelection is added on line 110 in frontend/src/state/store/index.tsx
          // but hasSelection does not exist on IStore
          delete s.hasSelection;
        }

        const value = typeof newStore === 'function' ? newStore(s) : newStore;

        if (isEqual(value, s)) {
          return s;
        }

        return value;
      }),
    []
  );

  const resetStore = useCallback<IStoreContext['resetStore']>(() => setStore(null), [setStore]);

  const onConfirmStoreChange = useCallback(
    async ({ newStore, callback }) => {
      setStore(newStore);
      resetLastTimeStoreUpdated();

      if (typeof callback === 'function') {
        await callback();
      }
    },
    [setStore]
  );

  /**
   * Helper function that extracts data from store.
   */
  const getStoreInformation: GetStoreInformation = useCallback(
    property => store?.[property] ?? null,
    [store]
  );

  useEffect(() => {
    LocalStorage.setItem(StorageKeys.STORE_VERSION_2, {
      store
    });
  }, [store]);

  // @ts-ignore will fix the `store` type in the next pr.
  return useMemo(
    () => ({
      onConfirmStoreChange,
      store: remappedStore,
      resetStore,
      setStore,
      getStoreInformation,
      // Store selection 2.0 allows selection of any store
      selectUnavailableStore: setStore
    }),
    [getStoreInformation, onConfirmStoreChange, remappedStore, resetStore, setStore]
  );
};
